var AbstractPlayerTech        = require("./Tech/Abstract/AbstractPlayerTech"),
    AbstractPlayerView        = require("./AbstractPlayerView"),
    AdsRequiredView           = require("./AdsRequiredView"),
    AudioView                 = require("./AudioView"),
    CategoryRepository        = require("app/Domain/Ott/Api/V2/DataAccess/Category/Repository"),
    DataTransformer           = require("./DataTransformer"),
    FatalErrorView            = require("./FatalErrorView"),
    ImageReader               = require("./Tech/ImageReader/Reader"),
    ImageView                 = require("./ImageView"),
    ImageViewer               = require("./Tech/ImageViewer/ImageViewer"),
    InvalidGeoView            = require("./InvalidGeoView"),
    LoaderView                = require("./LoaderView"),
    LockedView                = require("./LockedView"),
    MediaNotFoundView         = require("./MediaNotFoundView"),
    MediaPlayerController     = require("app/core/MediaPlayerController"),
    MediaRepository           = require("app/Domain/Ott/Api/V2/DataAccess/Media/Repository"),
    PlaylistRepository        = require("app/Domain/Ott/Api/V2/DataAccess/Playlist/Repository"),
    ReaderView                = require("./ReaderView"),
    RmpIframeAudioPlayer      = require("./Tech/RadiantMediaPlayer/IframeAudioPlayer"),
    RmpVideoPlayer            = require("./Tech/RadiantMediaPlayer/VideoPlayer"),
    TooManySessionsView       = require("./TooManySessionsView"),
    UnsupportedMediaView      = require("./UnsupportedMediaView"),
    UnsuspendView             = require("./UnsuspendView"),
    VideoView                 = require("./VideoView"),
    VimeoPlayer               = require("./Tech/VimeoPlayer/Player"),
    YouTubePlayer             = require("./Tech/YouTubePlayer/Player"),
    circularSafeJSONStringify = require("app/utils/circularSafeJSONStringify"),
    deepEqual                 = require("fast-deep-equal"),
    findRootMedia             = require("app/utils/findRootMedia"),
    get                       = require("object-path").get,
    kind                      = require("enyo/kind"),
    logger                    = require("enyo/logger"),
    quickMacroize             = require("enyo/macroize").quickMacroize;

module.exports = kind(/** @lends  app.MediaPlayer.MediaPlayerController.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.MediaPlayerController",

    /**
     * @private
     */
    kind: MediaPlayerController,

    /**
     * All instantiated media player views.
     *
     * @type {Array}
     * @private
     */
    playerViews: null,

    /**
     * All instantiated media player tech.
     *
     * @type {Array}
     * @private
     */
    playerTechs: null,

    /**
     * @private
     */
    handlers: {
        onMediaPlayerError:          "onMediaPlayerError",
        onProgressTrackerError:      "onProgressTrackerError",
        onRequestShowLoadingGraphic: "onRequestShowLoadingGraphic",
        onRequestHideLoadingGraphic: "onRequestHideLoadingGraphic"
    },

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            var displayMode;
            this.playerViews = [];
            this.playerTechs = [];
            sup.apply(this, arguments);

            displayMode = this.app.$.state.mediaPlayerDisplayMode;
            this.set("displayMode", displayMode ? displayMode : "maximized");
        };
    }),

    // Controller Activation
    // --------------------------------

    /**
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the page is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the page failed to activate.
     * @public
     */
    execute: function(activation) {
        var app                     = this.app,
            $appState               = app.$.state,
            $authService            = app.$.authService,
            $watchingSessionService = app.$.watchingSessionService,
            defaultLanguage         = app.$.i18nService.getDefaultLanguage(),
            language                = get(activation, "params.language", defaultLanguage),
            mediaType               = get(activation, "params.mediaType", null),
            mediaSlug               = get(activation, "params.mediaSlug", null),
            mediaId                 = get(activation, "params.mediaId", null),
            artistSlug              = get(activation, "params.artistSlug", null),
            playlistSlug            = get(activation, "params.playlistSlug", null),
            categorySlug            = get(activation, "params.categorySlug", null),
            autoplay                = get(activation, "params.autoplay", true),
            cursor                  = get(activation, "params.cursor", null),
            skipCache               = get(activation, "params.skipCache", false),
            mediaPlayer,
            mediaPromise,
            playlistPromise,
            categoryPromise,
            sessionPromise,
            prevMediaPromise,
            nextMediaPromise;

        // We're already playing this media
        if (this.isAlreadyPlayingRequestedMedia(
            language,
            mediaType,
            mediaSlug,
            mediaId,
            artistSlug,
            playlistSlug,
            categorySlug
        ) && !skipCache) {
            if (cursor !== null) {
                mediaPlayer      = $appState.get("mediaPlayer");
                mediaPlayer.seek && mediaPlayer.seek(cursor);
            }
            activation.resolve(1);

            return;
        }

        // Reset state
        $appState.resetMediaPlayerState();
        $appState.set("mediaPlayerController", this);

        // Loading graphic
        this.onRequestShowLoadingGraphic();

        // Load media
        if (mediaSlug) {
            mediaPromise = MediaRepository.getByTypeAndSlug(skipCache, language, mediaType, mediaSlug);
        }
        else if (mediaId) {
            mediaPromise = MediaRepository.getByTypeAndId(skipCache, language, mediaType, mediaId);
        }

        // Load Playlist
        playlistPromise = playlistSlug
            ? PlaylistRepository.getBySlug(skipCache, language, playlistSlug)
            : PlaylistRepository.noop();

        // Load Category
        categoryPromise = categorySlug
            ? CategoryRepository.getBySlug(skipCache, language, categorySlug)
            : CategoryRepository.noop();

        // Get watching session
        sessionPromise = Promise.all([playlistPromise, categoryPromise])
            .then(this.bindSafely(function(responses) {
                var playlistResponse = responses[0],
                    categoryResponse = responses[1],
                    playlistId,
                    isAdult;

                playlistId = get(playlistResponse, "data.id", null)
                    || get(categoryResponse, "data.id", null)
                    || null;
                isAdult    = Boolean($authService.isAdult());

                return $watchingSessionService.requestSession(language, mediaType, mediaSlug, mediaId, playlistId, isAdult);
            }));

        // Get previous media
        prevMediaPromise = sessionPromise
            .then(this.bindSafely(function(response) {
                var type,
                    slug,
                    id;

                type = get(response, "data.prev.type", null);
                slug = get(response, "data.prev.slug", null);
                id   = get(response, "data.prev.id", null);

                if (type && slug) {
                    return MediaRepository.getByTypeAndSlug(skipCache, language, type, slug);
                }
                else if (type && id) {
                    return MediaRepository.getByTypeAndId(skipCache, language, type, id);
                }
                else {
                    return MediaRepository.noop();
                }
            }));

        // Get next media
        nextMediaPromise = sessionPromise
            .then(this.bindSafely(function(response) {
                var type,
                    slug,
                    id;

                type = get(response, "data.next.type", null);
                slug = get(response, "data.next.slug", null);
                id   = get(response, "data.prev.id", null);

                if (type && slug) {
                    return MediaRepository.getByTypeAndSlug(skipCache, language, type, slug);
                }
                else if (type && id) {
                    return MediaRepository.getByTypeAndId(skipCache, language, type, id);
                }
                else {
                    return MediaRepository.noop();
                }
            }));

        // Media loaded, let's determine which view we need
        Promise.all([
            mediaPromise,
            categoryPromise,
            sessionPromise,
            prevMediaPromise,
            nextMediaPromise
        ])

            // Show View
            .then(this.bindSafely(function(responses) {
                var mediaResponse     = responses[0],
                    categoryResponse  = responses[1],
                    sessionResponse   = responses[2],
                    prevMediaResponse = responses[3],
                    nextMediaResponse = responses[4],
                    mediaData         = null,
                    categoryData      = null,
                    sessionData       = null,
                    prevMediaData     = null,
                    nextMediaData     = null,
                    PlayerViewKind,
                    PlayerTechKind;

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                // Prepare data
                if (mediaResponse.status === "ok") {
                    mediaData = DataTransformer.transformMedia(language, mediaResponse.data, {
                        artistSlug:   artistSlug,
                        playlistSlug: playlistSlug,
                        categorySlug: categorySlug
                    });
                }

                if (categoryResponse && categoryResponse.status === "ok") {
                    categoryData = DataTransformer.transformCategory(language, categoryResponse.data);
                }

                if (sessionResponse.status === "ok") {
                    sessionData = DataTransformer.transformWatchingSession(language, mediaType, sessionResponse.data);
                }

                if (sessionResponse.status === "ok" && mediaResponse.status === "ok") {
                    mediaData = DataTransformer.addSessionDataToAsset(mediaData, sessionData);
                }

                if (prevMediaResponse.status === "ok") {
                    prevMediaData = DataTransformer.transformMedia(language, prevMediaResponse.data, {
                        artistSlug:   artistSlug,
                        playlistSlug: playlistSlug,
                        categorySlug: categorySlug
                    });
                }

                if (nextMediaResponse.status === "ok") {
                    nextMediaData = DataTransformer.transformMedia(language, nextMediaResponse.data, {
                        artistSlug:   artistSlug,
                        playlistSlug: playlistSlug,
                        categorySlug: categorySlug
                    });
                }

                // We're definitely not going to show the player
                if (mediaResponse.status !== "ok" || sessionResponse.status !== "ok") {
                    this.deactivatePlayer();

                    // Just enough state to show error messages
                    this.updateState(
                        activation.params,
                        mediaData
                    );

                    // Handle problems with the watching session.
                    if (sessionResponse.status !== "ok") {
                        switch (sessionResponse.type) {
                            case "user_required":
                                activation.resolve(-1, this.bindSafely(function() {
                                    this.requireUser(language, mediaType);
                                }));

                                return;
                            case "premium":
                                activation.resolve(-1, this.bindSafely(function() {
                                    this.requireUpgrade(language);
                                }));

                                return;
                            case "suspended":
                                this.updateView(UnsuspendView);
                                break;
                            case "locked":
                                this.updateView(LockedView);
                                break;
                            case "invalid_geo":
                                this.updateView(InvalidGeoView);
                                break;
                            case "media_not_found":
                            case "asset_not_found":
                                this.updateView(MediaNotFoundView);
                                break;
                            case "server_error":
                            case "server_timeout_error":
                            case "bad_request_error":
                            default:
                                activation.resolve(-1, this.bindSafely(function() {
                                    this.requireOopsPage(language);
                                }));

                                return;
                        }
                    }

                    // Problem fetching media
                    if (mediaResponse.status !== "ok") {
                        switch (mediaResponse.type) {
                            case "not_found_error":
                                this.updateView(MediaNotFoundView);
                                break;
                            case "server_error":
                            case "server_timeout_error":
                            case "bad_request_error":
                            default:
                                activation.resolve(-1, this.bindSafely(function() {
                                    this.requireOopsPage(language);
                                }));

                                return;
                        }
                    }

                    // If we're still here, that means we're showing an error
                    // view and we can update the documentTitle.
                    this.updateDocumentTitle();

                    // And that's it. We're done for now.
                    activation.resolve(1);

                    return;
                }

                // All good, let's figure out how we're going to build our
                // player.
                PlayerViewKind = this.resolvePlayerViewKind(mediaData);
                PlayerTechKind = this.resolvePlayerTechKind(mediaData);

                if (!PlayerViewKind || !PlayerTechKind) {

                    // Just enough state to show error messages
                    this.updateState(
                        activation.params,
                        mediaData
                    );
                    this.updateView(UnsupportedMediaView);
                }
                else {
                    // Next we activate the player. This sets up the correct
                    // view and tech.
                    this.activatePlayer(PlayerViewKind, PlayerTechKind, mediaData, sessionData);

                    // Now that everything is set, we apply all the media data
                    // to the state. This should trigger playback of the media.
                    this.updateState(
                        activation.params,
                        mediaData,
                        categoryData,
                        sessionData,
                        prevMediaData,
                        nextMediaData,
                        autoplay
                    );
                }

                // If we're still here, that means we're showing a media player
                // and we can update the documentTitle.
                this.updateDocumentTitle();
                activation.resolve(1);
            }))

            // Oops
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                activation.resolve(-1, this.bindSafely(function() {
                    this.requireOopsPage(language);
                }));
            }))

            // No more loading graphic
            .finally(this.bindSafely(function() {
                this.onRequestHideLoadingGraphic();
            }));
    },

    /**
     * Test if the MediaPlayer is already playing the requested item.
     *
     * @param    {string}   language      The media language
     * @param    {string}   mediaType     The media type
     * @param    {string=}  mediaSlug     The media slug
     * @param    {string=}  mediaId       The media id
     * @param    {string=}  artistSlug    The artist slug
     * @param    {string=}  playlistSlug  The playlist slug
     * @param    {string=}  categorySlug  The category slug
     * @returns  {boolean}  Whether the player is already playing the requested
     *                      item.
     * @private
     */
    isAlreadyPlayingRequestedMedia: function(
        language,
        mediaType,
        mediaSlug,
        mediaId,
        artistSlug,
        playlistSlug,
        categorySlug
    ) {
        var $appState = this.app.$.state;

        if ($appState.get("mediaPlayerMediaLanguage") !== language) {
            return false;
        }

        if ($appState.get("mediaPlayerMediaType") !== mediaType) {
            return false;
        }

        if ($appState.get("mediaPlayerMediaSlug") !== mediaSlug
            && $appState.get("mediaPlayerMediaId") !== mediaId) {
            return false;
        }

        if ($appState.get("mediaPlayerArtistSlug") !== artistSlug) {
            return false;
        }

        if ($appState.get("mediaPlayerPlaylistSlug") !== playlistSlug) {
            return false;
        }

        if ($appState.get("mediaPlayerCategorySlug") !== categorySlug) {
            return false;
        }

        return true;
    },

    /**
     * @param  {?Object}  params         The params passed to this controller
     * @param  {?Object}  mediaData      The media data
     * @param  {?Object}  categoryData   The category data
     * @param  {?Object}  sessionData    The watching session
     * @param  {?Object}  prevMediaData  The media data of the previous
     *                                   mediaItem
     * @param  {?Object}  nextMediaData  The media data of the next mediaItem
     * @param  {string=}  autoplay       Whether to autoplay
     * @private
     */
    updateState: function(
        params,
        mediaData,
        categoryData,
        sessionData,
        prevMediaData,
        nextMediaData,
        autoplay
    ) {
        var state           = {},
            $appState       = this.app.$.state,
            rootData        = findRootMedia(mediaData),
            defaultLanguage = this.app.$.i18nService.getDefaultLanguage();

        state.params = params || {};

        if (mediaData) {
            state.media = mediaData;
            state.asset = mediaData.assets.find(function(asset) {
                return asset.type === "primary";
            }) || null;
        }
        else {
            state.media = null;
            state.asset = null;
        }

        state.category        = categoryData || null;
        state.watchingSession = sessionData || null;
        state.prevMedia       = prevMediaData || null;
        state.nextMedia       = nextMediaData || null;
        state.autoplay        = autoplay === false || autoplay === undefined ? false : true;

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }

        // Broadcast state changes to app state
        $appState.set("mediaPlayerMediaLanguage", get(params, "language", defaultLanguage));
        $appState.set("mediaPlayerRootType", get(rootData, "type", null));
        $appState.set("mediaPlayerRootSlug", get(rootData, "meta.slug", null));
        $appState.set("mediaPlayerRootId", get(rootData, "id", null));
        $appState.set("mediaPlayerMediaType", get(mediaData, "type", null));
        $appState.set("mediaPlayerMediaSlug", get(mediaData, "meta.slug", null));
        $appState.set("mediaPlayerMediaId", get(mediaData, "id", null));
        $appState.set("mediaPlayerArtistSlug", get(params, "artistSlug", null));
        $appState.set("mediaPlayerPlaylistSlug", get(params, "playlistSlug", null));
        $appState.set("mediaPlayerCategorySlug", get(params, "categorySlug", null));
    },

    /**
     * Update the document title.
     *
     * @private
     */
    updateDocumentTitle: function() {
        var i18nService = this.app.$.i18nService,
            state       = this.state || {},
            title       = get(state, "media.meta.title", null);

        if (this.app.get("documentTitleOwner") !== this) {
            return;
        }

        if (title) {
            this.app.setDocumentTitle(i18nService.getString("app.MediaPlayer.Document.Title.title", {
                title: String(title || "")
            }));
        }
        else {
            this.app.setDocumentTitle(i18nService.getString("app.MediaPlayer.Document.Title.noTitle"));
        }
    },

    /**
     * @param  {number}  wasActive  The previous value
     * @protected
     */
    activeChanged: function(wasActive) {
        var isActive              = this.active,
            app                   = this.app,
            $appState             = this.app.$.state,
            $installPromptService = app.$.installPromptService;

        if ($appState.get("mediaPlayerController") === this) {
            $appState.set("mediaPlayerActive", this.get("active"));
        }

        // Block install prompt while the player is active
        if (wasActive !== 1 && isActive === 1) {
            $installPromptService.blockInstallPrompt();
        }
        else if (wasActive !== -1 && isActive === -1) {
            $installPromptService.unblockInstallPrompt();
        }
    },

    /**
     * @private
     */
    displayModeChanged: function() {
        var currentView = this.view,
            displayMode = this.displayMode;

        // If we have a player and it does not support the displayMode, we
        // auto-deactivate.
        if (currentView
            && currentView instanceof AbstractPlayerView
            && !currentView.supportsDisplayMode(displayMode)
        ) {
            this.deactivate();
        }

        // From here we broadcast our new displayMode
        this.app.$.state.set("mediaPlayerDisplayMode", displayMode);
    },

    // Player Activation
    // --------------------------------

    /**
     * Sets up a player view and player tech capable of playing the provided
     * media. If we have reusable views and tech available we'll re-use those
     * otherwise new ones will be instantiated.
     *
     * @param  {Function}  PlayerViewKind   The view kind required
     * @param  {Function}  PlayerTechKind   The tech kind required
     * @param  {?Object}   mediaData        The media data
     * @param  {?Object}   sessionData      The watching session
     * @private
     */
    activatePlayer: function(
        PlayerViewKind,
        PlayerTechKind,
        mediaData,
        sessionData
    ) {
        var playerView,
            playerTech,
            nonPlayerView;

        // Deactivate incompatible player tech as needed
        playerTech = this.getCurrentPlayerTech();

        if (playerTech) {
            if (
                !(playerTech instanceof PlayerTechKind)
                || !playerTech.isReusableFor(mediaData, sessionData)
            ) {
                this.deactivatePlayerTech();
            }
        }

        // Deactivate incompatible player view as needed
        playerView = this.getCurrentPlayerView();

        if (playerView) {
            if (!(playerView instanceof PlayerViewKind)) {
                this.deactivatePlayerView();
            }
        }

        // If we have a non-player view, destroy it
        if (!playerView) {
            nonPlayerView = this.view;

            if (nonPlayerView) {
                this.destroyView(nonPlayerView);
            }
        }

        // Activate new player view as needed
        playerView = this.getCurrentPlayerView();

        if (!playerView) {
            this.activatePlayerView(PlayerViewKind);
        }

        // Activate new player tech as needed
        playerTech = this.getCurrentPlayerTech();

        if (!playerTech) {
            this.activatePlayerTech(PlayerTechKind, mediaData, sessionData);
        }
    },

    /**
     * Set up the correct player view.
     *
     * @param  {Function}  PlayerViewKind  The view kind required
     * @private
     */
    activatePlayerView: function(PlayerViewKind) {
        var playerView;

        // Existing player view must be deactivated first
        if (this.getCurrentPlayerView()) {
            throw new Error("Deactivate existing player view before activating another");
        }

        // We have another non-player view. Time to destroy it.
        if (this.view) {
            throw new Error("Destroy existing view before activating a player view");
        }

        // Look for existing reusable view.
        playerView = this.findPlayerView(PlayerViewKind);

        // We need to instantiate a new view
        if (!playerView) {
            playerView = this.createView(PlayerViewKind);
            playerView && this.registerPlayerView(playerView);
        }

        // Somehow we cannot create the PlayerView
        if (!playerView) {
            throw new Error("Cannot create Player View");
        }

        // Player view doesn't support the current displayMode
        if (!playerView.supportsDisplayMode(this.displayMode)) {
            // TODO: Do we throw an error here?
            this.deactivate();

            return;
        }

        // Mount the view.
        this.mountView(playerView);
    },

    /**
     * Set up the correct player tech.
     *
     * @param  {Function}  PlayerTechKind   The tech kind required
     * @param  {?Object}   mediaData        The media data
     * @param  {?Object}   sessionData      The watching session
     * @private
     */
    activatePlayerTech: function(PlayerTechKind, mediaData, sessionData) {
        var playerView,
            playerTech;

        // Existing player tech must be deactivated first
        if (this.getCurrentPlayerTech()) {
            throw new Error("Deactivate existing player tech before activating another");
        }

        // Player view required
        playerView = this.getCurrentPlayerView();

        if (!playerView) {
            throw new Error("Cannot activate player tech: Player view missing.");
        }

        // Look for existing reusable tech.
        playerTech = this.findPlayerTech(PlayerTechKind, mediaData, sessionData);

        // New player tech required
        if (!playerTech) {
            playerTech = this.createPlayerTech(PlayerTechKind);
            playerTech && this.registerPlayerTech(playerTech);
        }

        // Somehow we cannot create the PlayerTech
        if (!playerTech) {
            throw new Error("Cannot create Player Tech");
        }

        // Reusable tech found.
        playerView.mountPlayerTech(playerTech);
        this.app.$.state.set("mediaPlayer", playerTech);
    },

    /**
     * @param    {Function}      PlayerTechKind  The player tech kind
     * @returns  {enyo.Control}  The player tech
     * @private
     */
    createPlayerTech: function(PlayerTechKind) {
        return this.createComponent({
            kind:       PlayerTechKind,
            container:  false,
            controller: this,
            state:      null
        });
    },

    // Player View Juggling
    // --------------------------------

    /**
     * Returns the current view if we have one.
     *
     * @returns  {AbstractPlayerView|null}  The player view if available, `null`
     *                                      otherwise.
     */
    getCurrentPlayerView: function() {
        var view = this.view;

        return view instanceof AbstractPlayerView ? view : null;
    },

    /**
     * Resolve which kind of player view we need.
     *
     * @param    {Object}     mediaData  The media data
     * @returns  {?Function}  The view kind, if any
     * @private
     */
    resolvePlayerViewKind: function(mediaData) {
        var mediaType = get(mediaData, "type", null);

        switch (mediaType) {
            case "album_track":
            case "audio":
            case "podcast_episode":
                return AudioView;
            case "episode":
            case "video":
                return VideoView;
            case "book":
            case "issue":
                return ReaderView;
            case "photo":
                return ImageView;
            default:
                return null;
        }
    },

    /**
     * Looks for an existing Player View of the requested kind and returns it if
     * found.
     *
     * @param    {Function}       PlayerViewKind  The kind constructor of the
     *                                            view
     * @returns  {?enyo.Control}  The view if found, `null` otherwise
     * @private
     */
    findPlayerView: function(PlayerViewKind) {
        return this.playerViews.find(function(view) {
            return view instanceof PlayerViewKind;
        }) || null;
    },

    /**
     * Keep a new view instance around for reuse.
     *
     * @param  {enyo.Control}  view  The view to register.
     * @private
     */
    registerPlayerView: function(view) {
        this.playerViews.push(view);
    },

    /**
     * Remove the view instance.
     *
     * @param  {enyo.Control}  view  The view to unregister.
     * @private
     */
    unregisterPlayerView: function(view) {
        var index;

        index = this.playerViews.findIndex(function(registeredView) {
            return registeredView === view;
        });

        if (index !== -1) {
            this.playerViews.splice(index, 1);
        }
    },

    /**
     * @private
     */
    viewChanged: function() {
        var view = this.view;

        // No audio or video player, no autoplaying
        if (view
            && !(view instanceof VideoView)
            && !(view instanceof AudioView)) {
            this.app.$.autoplayTimerService.stopTimer();
        }

        // No player view, reset player view state
        if (view && (!(view instanceof AbstractPlayerView))) {
            this.app.$.state.resetMediaPlayerViewState();
        }
    },

    // Player Tech Juggling
    // --------------------------------

    /**
     * Returns the current tech if we have one.
     *
     * @returns  {AbstractPlayerView|null}  The player tech if available, `null`
     *                                      otherwise.
     */
    getCurrentPlayerTech: function() {
        var playerView = this.getCurrentPlayerView();

        return playerView
            ? playerView.getPlayerTech() || null
            : null;
    },

    /**
     * Resolve which kind of player implementation we need.
     *
     * @param    {Object}     mediaData  The media data
     * @returns  {?Function}  The player kind, if any
     * @private
     */
    resolvePlayerTechKind: function(mediaData) {
        var $watchingSessionService = this.app.$.watchingSessionService,
            mediaType               = get(mediaData, "type", null),
            assets                  = get(mediaData, "assets", []),
            primaryAsset            = assets.find(function(asset) {
                return asset.type === "primary";
            }) || null,
            format                  = get(primaryAsset, "format", null),
            platform                = get($watchingSessionService, "session.platform", null);

        // Audio content
        if (["album_track", "audio", "podcast_episode"].includes(mediaType)) {
            return RmpIframeAudioPlayer;
        }

        // Video content
        if (["episode", "video"].includes(mediaType)) {
            if (format === "video") {
                return RmpVideoPlayer;
            }
            else if (format === "external_video") {
                switch (platform) {
                    case "youtube":
                        return YouTubePlayer;
                    case "vimeo":
                        return VimeoPlayer;
                    case "streamable":
                    case "livestream":
                        return RmpVideoPlayer;
                    default:
                        return null;
                }
            }
        }

        // Reader content
        if (["book", "issue"].includes(mediaType)) {
            return ImageReader;
        }

        // Image content
        if (mediaType === "photo") {
            return ImageViewer;
        }

        return null;
    },

    /**
     * Looks for an existing Player Tech of the requested kind and returns it if
     * found.
     *
     * @param    {Function}       PlayerTechKind  The kind constructor of the
     *                                            tech
     * @param    {?Object}        mediaData       The media data
     * @param    {?Object}        sessionData     The watching session
     * @returns  {?enyo.Control}  The tech if found, `null` otherwise
     * @private
     */
    findPlayerTech: function(PlayerTechKind, mediaData, sessionData) {
        return this.playerTechs.find(function(tech) {
            return tech instanceof PlayerTechKind
                && tech.isReusableFor(mediaData, sessionData);
        }) || null;
    },

    /**
     * Keep a new tech instance around for reuse.
     *
     * @param  {enyo.Control}  tech  The tech to register.
     * @private
     */
    registerPlayerTech: function(tech) {
        this.playerTechs.push(tech);
    },

    /**
     * Remove the tech instance.
     *
     * @param  {enyo.Control}  tech  The tech to unregister.
     * @private
     */
    unregisterPlayerTech: function(tech) {
        var index;

        index = this.playerTechs.findIndex(function(registeredTech) {
            return registeredTech === tech;
        });

        if (index !== -1) {
            this.playerTechs.splice(index, 1);
        }
    },

    // Player Event Handling
    // --------------------------------

    /**
     * Handle Player-related errors that bubbled up from the view layer.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @private
     */
    onMediaPlayerError: function(sender, event) {
        logger.error(event.error);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error("Media Player Error"), {
                extra: {
                    event: circularSafeJSONStringify(event)
                }
            });
        }

        switch (event.type) {
            case "ads_required_error":
                this.deactivatePlayer();
                this.updateView(AdsRequiredView);
                break;
            case "cannot_load_image":
            case "missing_audio_source":
            case "missing_video_source":
            case "player_setup_error":
                this.deactivatePlayer();
                this.updateView(UnsupportedMediaView);
                break;
            case "fatal_player_error":
                this.destroyPlayer();
                this.updateView(FatalErrorView);
                break;
            case "player_error":
            default:
                this.requireOopsPage(this.app.$.i18nService.get("language"));
                break;
        }
    },

    /**
     * Handle ProgressTracker-related errors that bubbled up from the view layer.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @private
     */
    onProgressTrackerError: function(sender, event) {
        switch (event.type) {
            case "invalid_session":
            case "too_many_sessions":
                this.deactivatePlayer();
                this.updateView(TooManySessionsView);
                break;
            default:
                this.requireOopsPage(this.app.$.i18nService.get("language"));
                break;
        }
    },

    /**
     * @private
     */
    loadingGraphicRequests: 0,

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    onRequestShowLoadingGraphic: function() {
        this.set("loadingGraphicRequests", this.loadingGraphicRequests + 1);

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    onRequestHideLoadingGraphic: function() {
        this.set("loadingGraphicRequests", this.loadingGraphicRequests - 1);

        return true;
    },

    /**
     * Ensures a loading graphic view exists if we don't have a view already, or
     * if the current view is not a player.
     *
     * @param  {number}  oldCount  The old number of loading graphic requests
     * @private
     */
    loadingGraphicRequestsChanged: function(oldCount) {
        var view     = this.view,
            newCount = this.loadingGraphicRequests;

        if (oldCount < 1 && newCount >= 1) {
            if (!view || !(view instanceof AbstractPlayerView)) {
                this.updateView(LoaderView);
            }
        }
    },

    // Player Deactivation
    // --------------------------------

    /**
     * Unmount player view and tech. If they're not reusable, they will be
     * destroyed.
     *
     * @private
     */
    deactivatePlayer: function() {
        this.deactivatePlayerTech();
        this.deactivatePlayerView();

        // No player, no autoplaying
        this.app.$.autoplayTimerService.stopTimer();
    },

    /**
     * Unmounts the player tech, keeps it around for reuse if possible, destroys
     * it otherwise.
     *
     * @private
     */
    deactivatePlayerTech: function() {
        var playerView = this.getCurrentPlayerView(),
            playerTech = this.getCurrentPlayerTech();

        if (playerTech) {
            playerTech.stop && playerTech.stop();
            playerTech.resetPlayerState();
            playerView && playerView.unmountPlayerTech(playerTech);
            playerTech.set("state", null);

            if (!playerTech.get("isReusable")) {
                this.unregisterPlayerTech(playerTech);
                playerTech.destroy();
            }
        }
    },

    /**
     * Unmounts the player view, keeps it around for reuse if possible, destroys
     * it otherwise.
     *
     * @private.
     */
    deactivatePlayerView: function() {
        var playerView = this.getCurrentPlayerView(),
            playerTech = this.getCurrentPlayerTech();

        // Deactivate player tech first
        if (playerTech) {
            throw new Error("Deactivate player tech before deactivating player view");
        }

        // Now we can deactivate the view
        if (playerView instanceof AbstractPlayerView) {
            this.unmountView(playerView);
            playerView.set("state", null);

            if (!playerView.get("isReusable")) {
                this.unregisterPlayerView(playerView);
                this.destroyView(playerView);
            }
        }
    },

    /**
     * Completely destroys the player view and player tech, regardless of reuse
     * support. This should only be used in case of a fatal error where the
     * player must be rebuilt.
     *
     * @private
     */
    destroyPlayer: function() {
        var playerView = this.view,
            playerTech;

        // Mark PlayerView as non-reusable. Obtain Player Tech.
        if (playerView instanceof AbstractPlayerView) {
            playerView.set("isReusable", false);
            playerTech = playerView.getPlayerTech();
        }

        // Mark Player Tech as non-reusable.
        if (playerTech instanceof AbstractPlayerTech) {
            playerTech.set("isReusable", false);
        }

        // Deactivate. Non-reusable parts will be destroyed.
        this.deactivatePlayer();
    },

    // Controller Deactivation
    // --------------------------------

    /**
     * Stops any pending activation cycle, remove views from DOM and clear
     * state. Any views that are reusable will be kept around on the
     * MediaPlayerManager.
     *
     * @public
     */
    deactivate: function() {
        var activation = this.activation;

        // Player is activating. Stop activation.
        if (activation && !activation.finished) {
            activation.resolve(-1);
        }

        // Deactivate the player, destroy any lingering view, clear state.
        this.deactivatePlayer();
        this.view && this.destroyView(this.view);
        this.updateState();
        this.set("active", -1);
    },

    // Redirects
    // --------------------------------

    /**
     * Redirect to user signup CTA
     *
     * @param  {string}  language   The media language
     * @param  {string}  mediaType  The media type
     * @private
     */
    requireUser: function(language, mediaType) {
        var $router      = this.app.$.router,
            redirectPath = $router.getCurrentPath() || "";

        redirectPath = "/" + redirectPath.replace(/^\/+/, "");
        redirectPath = encodeURIComponent(redirectPath);

        $router.redirect(quickMacroize("/{$language}/user-required/{$mediaType}/{$redirectPath}", {
            language:     language,
            mediaType:    mediaType.replace("_", "-"),
            redirectPath: redirectPath
        }));
    },

    /**
     * Redirect to user upgrade CTA
     *
     * @param  {string}  language  The media language
     * @private
     */
    requireUpgrade: function(language) {
        var $router      = this.app.$.router,
            redirectPath = $router.getCurrentPath() || "";

        redirectPath = "/" + redirectPath.replace(/^\/+/, "");
        redirectPath = encodeURIComponent(redirectPath);

        $router.redirect(quickMacroize("/{$language}/upgrade-required/{$redirectPath}", {
            language:     language,
            redirectPath: redirectPath
        }));
    },

    /**
     * Redirect to oops error page.
     *
     * @param  {string}  language  The media language
     * @private
     */
    requireOopsPage: function(language) {
        this.app.$.router.redirect(quickMacroize("/{$language}/oops", {
            language: language
        }));
    }

});
